import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function getToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function afssStatus(afss_due_date) {
  if (!afss_due_date) return null;
  const today = getToday();
  const due = new Date(afss_due_date);
  due.setHours(0, 0, 0, 0);
  if (due < today) return "overdue";
  const next30 = new Date(today);
  next30.setDate(today.getDate() + 30);
  if (due <= next30) return "due-soon";
  return "scheduled";
}

const CLICKABLE_STATUSES = new Set(["overdue", "due-soon", "scheduled", "done", null]);

function StatusBadge({ status, onClick, toggling }) {
  const cfg = {
    overdue:      { label: "Overdue",    bg: "#fee2e2", color: "#dc2626" },
    "due-soon":   { label: "Due Soon",   bg: "#fef3c7", color: "#d97706" },
    scheduled:    { label: "Scheduled",  bg: "#f1f5f9", color: "#64748b" },
    "fire-order": { label: "Fire Order", bg: "#ffedd5", color: "#ea580c" },
    exempt:       { label: "Exempt",     bg: "#f1f5f9", color: "#64748b" },
    done:         { label: "Done ✓",     bg: "#dcfce7", color: "#16a34a" },
  }[status] || { label: "No Date", bg: "#f1f5f9", color: "#94a3b8" };

  const clickable = !!onClick && CLICKABLE_STATUSES.has(status);
  const title = clickable ? (status === "done" ? "Click to undo" : "Click to mark as done") : undefined;

  return (
    <span
      onClick={clickable && !toggling ? onClick : undefined}
      title={title}
      style={{
        fontSize: "11px", fontWeight: 700, padding: "2px 8px",
        borderRadius: "10px", backgroundColor: cfg.bg, color: cfg.color,
        whiteSpace: "nowrap",
        cursor: clickable ? "pointer" : "default",
        opacity: toggling ? 0.5 : 1,
        transition: "opacity 0.15s",
        userSelect: "none",
      }}
    >{cfg.label}</span>
  );
}

function SiteRow({ site, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [dateVal, setDateVal] = useState(site.afss_due_date || "");
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/sites/${site.id}/`, { afss_due_date: dateVal || null });
      onSaved();
      setEditing(false);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleCancel = () => {
    setDateVal(site.afss_due_date || "");
    setEditing(false);
  };

  const handleToggleDone = async () => {
    if (toggling) return;
    setToggling(true);
    try {
      await axios.patch(`${API}/sites/${site.id}/`, { afss_submitted: !site.afss_submitted });
      onSaved();
    } catch { /* ignore */ }
    finally { setToggling(false); }
  };

  const status = site.under_fire_order ? "fire-order"
    : site.afss_exempt ? "exempt"
    : site.afss_submitted ? "done"
    : afssStatus(site.afss_due_date);

  return (
    <div style={{
      display: "grid", gridTemplateColumns: "2fr 1fr 180px 110px",
      padding: "10px 20px", borderBottom: `1px solid ${colors.border}`,
      alignItems: "center", gap: "12px", backgroundColor: colors.white,
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: "14px", color: colors.navy || "#1e3a5f" }}>
          {site.site_name}
        </div>
        <div style={{ fontSize: "12px", color: colors.slate, marginTop: "2px" }}>
          {site.site_address}
        </div>
      </div>
      <div style={{ fontSize: "13px", color: colors.slate }}>{site.account_name}</div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {editing ? (
          <>
            <input
              type="date"
              value={dateVal}
              onChange={(e) => setDateVal(e.target.value)}
              style={{ fontSize: "13px", padding: "4px 8px", border: `1px solid ${colors.border}`, borderRadius: "6px", flex: 1 }}
              autoFocus
            />
            <button onClick={handleSave} disabled={saving} style={{
              background: "#1e3a5f", color: "#fff", border: "none",
              borderRadius: "6px", padding: "4px 10px", fontSize: "12px",
              fontWeight: 600, cursor: "pointer",
            }}>{saving ? "…" : "Save"}</button>
            <button onClick={handleCancel} style={{
              background: "none", color: colors.slate, border: "none",
              fontSize: "12px", cursor: "pointer", padding: "4px",
            }}>✕</button>
          </>
        ) : (
          <button onClick={() => setEditing(true)} style={{
            background: "none", border: "none", cursor: "pointer",
            color: site.afss_due_date ? colors.slate : "#94a3b8",
            fontSize: "13px", textAlign: "left", padding: 0,
            textDecoration: "underline", textDecorationStyle: "dotted",
          }}>
            {site.afss_due_date
              ? new Date(site.afss_due_date).toLocaleDateString("en-AU")
              : "+ Set date"}
          </button>
        )}
      </div>
      <div>
        <StatusBadge status={status} onClick={handleToggleDone} toggling={toggling} />
      </div>
    </div>
  );
}

export function AFSSPage() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overdue");

  const loadSites = useCallback(() => {
    axios.get(`${API}/sites/`)
      .then((res) => setSites((res.data || []).filter(s => s.status === "active")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSites(); }, [loadSites]);

  const today = getToday();
  const next30 = new Date(today); next30.setDate(today.getDate() + 30);
  const next90 = new Date(today); next90.setDate(today.getDate() + 90);
  const thisMonth = today.getMonth();
  const thisYear = today.getFullYear();

  const tracked = sites.filter(s => !s.under_fire_order && !s.afss_exempt);

  const overdueSites = tracked.filter(s => {
    if (!s.afss_due_date || s.afss_submitted) return false;
    const d = new Date(s.afss_due_date); d.setHours(0, 0, 0, 0);
    return d < today;
  });

  const dueSites = tracked.filter(s => {
    if (!s.afss_due_date || s.afss_submitted) return false;
    const d = new Date(s.afss_due_date); d.setHours(0, 0, 0, 0);
    return d >= today && d <= next30;
  });

  const nextDueSites = tracked.filter(s => {
    if (!s.afss_due_date || s.afss_submitted) return false;
    const d = new Date(s.afss_due_date); d.setHours(0, 0, 0, 0);
    return d > next30 && d <= next90;
  });

  const monthSites = (monthIdx) => tracked.filter(s => {
    if (!s.afss_due_date) return false;
    const d = new Date(s.afss_due_date);
    return d.getMonth() === monthIdx;
  });

  const noDateSites = tracked.filter(s => !s.afss_due_date);

  const fireOrderSites = sites.filter(s => s.under_fire_order);
  const exemptSites = sites.filter(s => s.afss_exempt);

  const tabSites = (() => {
    if (tab === "overdue") return overdueSites;
    if (tab === "due") return dueSites;
    if (tab === "next-due") return nextDueSites;
    if (tab === "fire-order") return fireOrderSites;
    if (tab === "exempt") return exemptSites;
    const mIdx = parseInt(tab, 10);
    return monthSites(mIdx);
  })();

  const badge = (count, color = colors.red) => count > 0 ? (
    <span style={{
      marginLeft: "6px", fontSize: "10px", fontWeight: 700,
      backgroundColor: color, color: "#fff",
      borderRadius: "10px", padding: "1px 6px",
    }}>{count}</span>
  ) : null;

  const tabStyle = (id) => ({
    padding: "10px 14px",
    fontSize: "13px",
    fontWeight: tab === id ? 700 : 500,
    color: tab === id ? "#1e3a5f" : colors.slate,
    border: "none",
    borderBottom: tab === id ? "2px solid #1e3a5f" : "2px solid transparent",
    background: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
  });

  if (loading) return <div style={styles.loading}>Loading…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>AFSS — Annual Fire Safety Statements</div>
        <div style={{ fontSize: "13px", color: colors.slate }}>
          {overdueSites.length > 0 && (
            <span style={{ color: "#dc2626", fontWeight: 700, marginRight: "12px" }}>
              ⚠ {overdueSites.length} overdue
            </span>
          )}
          {sites.filter(s => s.afss_due_date).length} of {sites.length} sites with date set
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        display: "flex", overflowX: "auto",
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: colors.white,
        padding: "0 20px",
        flexShrink: 0,
      }}>
        <button style={tabStyle("overdue")} onClick={() => setTab("overdue")}>
          Overdue{badge(overdueSites.length)}
        </button>
        <button style={tabStyle("due")} onClick={() => setTab("due")}>
          Due{badge(dueSites.length, "#d97706")}
        </button>
        <button style={tabStyle("next-due")} onClick={() => setTab("next-due")}>
          Next Due{badge(nextDueSites.length, "#0891b2")}
        </button>
        <button style={tabStyle("fire-order")} onClick={() => setTab("fire-order")}>
          Under Fire Order{fireOrderSites.length > 0 ? badge(fireOrderSites.length, "#ea580c") : null}
        </button>
        {MONTHS.map((m, i) => (
          <button key={i} style={tabStyle(String(i))} onClick={() => setTab(String(i))}>
            {MONTH_SHORT[i]}
            {i === thisMonth && thisYear === today.getFullYear()
              ? badge(monthSites(i).length, "#0891b2")
              : monthSites(i).length > 0
                ? <span style={{ marginLeft: "4px", fontSize: "10px", color: colors.slate }}>({monthSites(i).length})</span>
                : null}
          </button>
        ))}
        <button style={tabStyle("exempt")} onClick={() => setTab("exempt")}>
          AFSS Exempt{exemptSites.length > 0 ? badge(exemptSites.length, "#64748b") : null}
        </button>
      </div>

      {/* Column headers */}
      <div style={{
        display: "grid", gridTemplateColumns: "2fr 1fr 180px 110px",
        padding: "7px 20px", backgroundColor: "#f8fafc",
        borderBottom: `1px solid ${colors.border}`,
        fontSize: "11px", fontWeight: 700, color: colors.slate,
        textTransform: "uppercase", letterSpacing: "0.4px", gap: "12px",
        flexShrink: 0,
      }}>
        <div>Site</div>
        <div>Account</div>
        <div>AFSS Due Date</div>
        <div>Status</div>
      </div>

      {/* Site rows */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {tabSites.length === 0 && (
          <div style={{ padding: "40px 20px", textAlign: "center", color: colors.slate, fontSize: "14px" }}>
            {tab === "overdue" ? "No overdue AFSS."
            : tab === "due" ? "No AFSS due in the next 30 days."
            : tab === "next-due" ? "No AFSS due in the next 3 months."
            : tab === "fire-order" ? "No sites currently under a fire order."
            : tab === "exempt" ? "No sites marked as AFSS exempt."
            : `No sites with AFSS due in ${MONTHS[parseInt(tab, 10)]}.`}
          </div>
        )}
        {tabSites
          .sort((a, b) =>
            (a.afss_submitted ? 1 : 0) - (b.afss_submitted ? 1 : 0) ||
            (a.afss_due_date || "").localeCompare(b.afss_due_date || "") ||
            a.site_name.localeCompare(b.site_name)
          )
          .map(s => <SiteRow key={s.id} site={s} onSaved={loadSites} />)}

        {/* No-date sites shown on Overdue tab */}
        {tab === "overdue" && noDateSites.length > 0 && (
          <>
            <div style={{
              padding: "8px 20px", backgroundColor: "#f8fafc",
              borderBottom: `1px solid ${colors.border}`,
              fontSize: "11px", fontWeight: 700, color: colors.slate,
              textTransform: "uppercase", letterSpacing: "0.4px",
            }}>
              No date set ({noDateSites.length} sites)
            </div>
            {noDateSites
              .sort((a, b) => a.site_name.localeCompare(b.site_name))
              .map(s => <SiteRow key={s.id} site={s} onSaved={loadSites} />)}
          </>
        )}
      </div>
    </div>
  );
}
